/**
 * @fileoverview Система логирования и мониторинга ошибок на базе Sentry.
 * Обеспечивает отслеживание ошибок, предупреждений и системных событий в TD Camera TWA.
 * 
 * @since 1.0.0
 */

import * as Sentry from '@sentry/react'
import packageJson from '../../package.json'

/**
 * Инициализирует систему мониторинга ошибок Sentry для Telegram Web App.
 * 
 * Настраивает автоматическое отслеживание ошибок, интеграцию с консолью
 * и привязку к текущей версии приложения для удобства отладки.
 * 
 * **Настройки мониторинга:**
 * - Автоматическое логирование ошибок и предупреждений консоли
 * - Привязка к релизной версии из package.json
 * - Экспериментальное включение расширенного логирования
 * 
 * @returns void
 * 
 * @remarks
 * Функция должна вызываться единожды при инициализации приложения,
 * желательно в корневом компоненте перед рендерингом интерфейса.
 * 
 * **Безопасность:**
 * DSN содержит публичный ключ Sentry, безопасный для клиентского кода.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * // Инициализация в главном компоненте App
 * import { initializeLogger } from './utils/logger';
 * 
 * function App() {
 *   useEffect(() => {
 *     initializeLogger();
 *   }, []);
 *   
 *   return <WebAppCam />;
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Использование после инициализации
 * import { logger } from './utils/logger';
 * 
 * try {
 *   riskyOperation();
 * } catch (error) {
 *   logger.error('Ошибка в операции', error);
 * }
 * ```
 * 
 * @see {@link https://docs.sentry.io/} для документации Sentry
 */
export function initializeLogger() {
  Sentry.init({
    dsn: 'https://eff5d8cfabc71526911e900c15ad58a8@o1076017.ingest.us.sentry.io/4509529673826304',
    release: packageJson.version,

    // Включаем логирование
    _experiments: {
      enableLogs: true
    },

    // Интеграции для автоматического логирования консоли
    integrations: [Sentry.consoleLoggingIntegration({levels: ['error', 'warn']})]
  })
}

/**
 * Объект логгера Sentry для ручного логирования событий и ошибок.
 * 
 * Предоставляет методы для записи сообщений различных уровней важности.
 * Автоматически интегрирован с системой мониторинга после инициализации.
 * 
 * **Доступные методы:**
 * - `logger.error()` - критические ошибки
 * - `logger.warn()` - предупреждения
 * - `logger.info()` - информационные сообщения
 * - `logger.debug()` - отладочная информация
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * // Логирование ошибки с контекстом
 * import { logger } from './utils/logger';
 * 
 * const handlePhotoCapture = async () => {
 *   try {
 *     await capturePhoto();
 *     logger.info('Фото успешно создано');
 *   } catch (error) {
 *     logger.error('Ошибка создания фото', {
 *       error,
 *       timestamp: Date.now(),
 *       userAgent: navigator.userAgent
 *     });
 *   }
 * };
 * ```
 * 
 * @example
 * ```tsx
 * // Логирование предупреждений геолокации
 * if (!position) {
 *   logger.warn('Геолокация недоступна, используем fallback');
 * }
 * ```
 * 
 * @see {@link initializeLogger} для инициализации системы логирования
 */
export const logger = Sentry.logger 